import { formatReferenceCopy } from 'utils/typography'
import './manager-executor-section.scss'

import {
  useEffect,
  useMemo,
  useState,
} from 'react'
import {
  useTranslation,
} from 'react-i18next'
import {
  Box,
  Button,
  Typography,
} from '@mui/material'

import {
  COLORS,
} from 'config/constants'

import ModalBig from '../../../../common/modalBig'
import ApplicationContactsBlock from './application-contacts-block'
import {
  buildExecutorContactFields,
  EXECUTOR_CONTACT_FIELD_KEYS,
  executorContactFormFromApp,
} from '../application-contacts-fields'

import {
  showManagerExecutorAssignForm,
  showManagerExecutorChangeForm,
} from '../detail-visibility'
import ManagerExecutorFields, {
  managerExecutorSelectionInvalid,
} from './manager-executor-fields'

function resolveExecutorDisplayName({
  app,
  executorId,
  companyOptions,
}: {
  app: ApplicationDetail
  executorId: number | ''
  companyOptions: ExecutorCompanyOption[]
}): string {
  const fromApp = app.executor_company?.name?.trim()
  if (fromApp) {
    return fromApp
  }
  const savedId = app.executor_company_id
  if (savedId != null) {
    const match = companyOptions.find((entry) => entry.id === savedId)
    if (match?.name?.trim()) {
      return match.name.trim()
    }
  }
  if (executorId !== '') {
    const match = companyOptions.find((entry) => entry.id === Number(executorId))
    if (match?.name?.trim()) {
      return match.name.trim()
    }
  }
  return '—'
}

const ManagerExecutorSection = ({
  app,
  managerView,
  executorId,
  onExecutorIdChange,
  executorChangeReason,
  onExecutorChangeReasonChange,
  companyOptions,
  runAction,
  onAssignExecutor,
}: ApplicationManagerExecutorProps) => {
  const { t } = useTranslation()
  const canAssign = showManagerExecutorAssignForm({ app, managerView })
  const canChange = showManagerExecutorChangeForm({ app, managerView })
  const showEditor = canAssign || canChange
  const hasSavedContacts = managerView && app.manager_id && EXECUTOR_CONTACT_FIELD_KEYS.some((key) => app[key]?.trim())
  const [ modalOpen, setModalOpen ] = useState(false)
  const [ modalExecutorId, setModalExecutorId ] = useState<number | ''>('')
  const [ modalChangeReason, setModalChangeReason ] = useState('')

  const displayName = useMemo(
    () => resolveExecutorDisplayName({ app, executorId, companyOptions }),
    [ app, executorId, companyOptions ],
  )

  const savedExecutorId = app.executor_company_id ?? null

  useEffect(() => {
    if (!modalOpen) {
      return
    }
    const initial = savedExecutorId ?? (executorId === '' ? '' : Number(executorId))
    setModalExecutorId(initial === null || initial === undefined ? '' : initial)
    setModalChangeReason('')
  }, [ modalOpen, savedExecutorId, executorId ])

  const modalSelectionInvalid = managerExecutorSelectionInvalid({
    app,
    executorId: modalExecutorId,
    executorChangeReason: modalChangeReason,
  })
  const modalNoChange = savedExecutorId !== null
    && modalExecutorId !== ''
    && Number(modalExecutorId) === savedExecutorId

  const openModal = () => {
    setModalOpen(true)
  }

  const closeModal = () => {
    setModalOpen(false)
  }

  const handleConfirm = () => {
    if (modalSelectionInvalid || modalNoChange) {
      return
    }
    void runAction(async () => {
      await onAssignExecutor({
        executor_company_id: Number(modalExecutorId),
        ...(modalChangeReason.trim() ? { change_reason: modalChangeReason.trim() } : {}),
      })
      onExecutorIdChange(modalExecutorId)
      onExecutorChangeReasonChange(modalChangeReason)
      setModalOpen(false)
    }, savedExecutorId ? 'applications__message__executorChanged' : 'applications__message__executorAssigned')
  }

  const modalTitle = canAssign && !savedExecutorId
    ? formatReferenceCopy(t('applications__text__executorAssignModalTitle'))
    : formatReferenceCopy(t('applications__text__executorChangeModalTitle'))

  const confirmLabel = canAssign && !savedExecutorId
    ? formatReferenceCopy(t('applications__text__assignExecutor'))
    : formatReferenceCopy(t('applications__text__saveExecutor'))

  const openButtonLabel = canAssign && !savedExecutorId
    ? formatReferenceCopy(t('applications__text__assignExecutor'))
    : formatReferenceCopy(t('applications__text__changeExecutorButton'))

  return (
    <div className='application-manager-executor-section'>
      {showEditor ? (
        <Box className='application-manager-executor-section__block'>
          {canAssign && !savedExecutorId && (
            <Typography variant='body2' sx={{ color: 'text.primary' }}>
              {formatReferenceCopy(t('applications__text__executorAssignMissingHint'))}
            </Typography>
          )}
          <div className='application-manager-executor-section__current'>
            <Typography variant='body2' sx={{
              color: COLORS.BLACK_DARK,
            }}>
              {displayName}
            </Typography>
            <Button variant='outlined' onClick={openModal}>
              <Typography component='span' variant='inherit'>
                {openButtonLabel}
              </Typography>
            </Button>
          </div>
          <ModalBig
            isOpen={modalOpen}
            onClose={closeModal}
            title={modalTitle}
            width={736}
            buttons={(
              <div className='application-manager-executor-section__modal-actions'>
                <Button
                  variant='outlined'
                  sx={{ minWidth: '238px', marginRight: 3, flexShrink: 0 }}
                  onClick={closeModal}
                >
                  <Typography component='span' variant='inherit'>
                    {formatReferenceCopy(t('actions__text__cancel'))}
                  </Typography>
                </Button>
                <Button
                  variant='contained'
                  fullWidth
                  disabled={modalSelectionInvalid || modalNoChange}
                  onClick={handleConfirm}
                >
                  <Typography component='span' variant='inherit'>
                    {confirmLabel}
                  </Typography>
                </Button>
              </div>
            )}
          >
            <ManagerExecutorFields
              app={app}
              executorId={modalExecutorId}
              onExecutorIdChange={setModalExecutorId}
              executorChangeReason={modalChangeReason}
              onExecutorChangeReasonChange={setModalChangeReason}
              companyOptions={companyOptions}
              denseTop
            />
          </ModalBig>
        </Box>
      ) : (
        <Typography variant='body2' sx={{
          color: COLORS.BLACK_DARK,
        }}>
          {displayName}
        </Typography>
      )}
      {hasSavedContacts && <ApplicationContactsBlock
        title={formatReferenceCopy(t('applications__text__mgrExecutorContactsTitle'))}
        fields={buildExecutorContactFields(executorContactFormFromApp(app), t)}
      />}
    </div>
  )
}
export default ManagerExecutorSection
